import * as React from 'react';

import type { SuccessFlagAction, dismissFlag, ActionsType } from '@helpcenter/flags';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import type { Flag, FlagAction } from 'state/ui/flags';
import { FlagGroup, AutoDismissFlag } from '@atlaskit/flag';
import ErrorIcon from '@atlaskit/icon/core/cross-circle';
import InfoIcon from '@atlaskit/icon/core/status-information';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

interface FlagGroupStatelessProps {
	flags: Flag[];
	dismissFlagAction: typeof dismissFlag;
}

const isSuccessFlagAction = (flagAction: FlagAction): flagAction is SuccessFlagAction => {
	return 'contentNode' in flagAction && !!flagAction.contentNode;
};

export class FlagGroupStateless extends React.Component<
	FlagGroupStatelessProps & WrappedComponentProps
> {
	handleFlagDismissed = (flagId: number) => {
		this.props.dismissFlagAction(flagId);
	};

	i18nFlagActions = (actions: FlagAction[]) => {
		const { intl } = this.props;

		return actions.map((action) => {
			return isSuccessFlagAction(action)
				? {
						content: action.contentNode,
						href: action?.href,
					}
				: {
						content: intl.formatMessage(action.content),
						href: action.href,
					};
		});
	};

	getIcon = (flag: Flag) => {
		switch (flag.type) {
			case 'error':
				return (
					<ErrorIcon
						spacing="spacious"
						color={token('color.icon.danger', '#BF2600')}
						label=""
						aria-hidden="true"
					/>
				);
			case 'warning':
				return (
					<WarningIcon
						spacing="spacious"
						color={token('color.icon.warning', '#FF8B00')}
						label=""
						aria-hidden="true"
					/>
				);
			case 'success':
				return (
					<SuccessIcon
						spacing="spacious"
						color={token('color.icon.success', '#36B37E')}
						label=""
						aria-hidden="true"
					/>
				);
			case 'bold':
				return (
					<InfoIcon
						spacing="spacious"
						color={token('color.icon.information', '#42526E')}
						label=""
						aria-hidden="true"
					/>
				);
			default:
				return (
					<ErrorIcon
						spacing="spacious"
						color={token('color.icon.danger', '#BF2600')}
						label=""
						aria-hidden="true"
					/>
				);
		}
	};

	renderFlag = (flag: Flag) => {
		const { intl } = this.props;
		if (flag.type === 'bold') {
			const dismissAction: ActionsType = [];
			if (flag.dismissActionValue) {
				dismissAction.push({
					content: intl.formatMessage(flag.dismissActionValue),
					onClick: () => {
						this.handleFlagDismissed(flag.id);
					},
				});
			}
			return (
				<AutoDismissFlag
					appearance={flag.appearance}
					id={flag.id}
					key={flag.id}
					icon={this.getIcon(flag)}
					title={intl.formatMessage(flag.titleMessage, flag.titleMessageValues)}
					description={
						flag.descriptionMessage === undefined
							? undefined
							: intl.formatMessage(flag.descriptionMessage, flag.descriptionMessageValues)
					}
					actions={[...flag.actions, ...dismissAction]}
				/>
			);
		}
		return (
			<AutoDismissFlag
				id={flag.id}
				key={flag.id}
				icon={this.getIcon(flag)}
				title={intl.formatMessage(flag.titleMessage, flag.titleMessageValues)}
				description={
					flag.descriptionMessage === undefined
						? undefined
						: intl.formatMessage(flag.descriptionMessage, flag.descriptionMessageValues)
				}
				actions={this.i18nFlagActions(flag.actions)}
			/>
		);
	};

	render() {
		const { flags } = this.props;
		return (
			<FlagGroup onDismissed={this.handleFlagDismissed}>
				{flags.map((flag) => this.renderFlag(flag))}
			</FlagGroup>
		);
	}
}

export default injectIntl(FlagGroupStateless);
